import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Modal as FlowbiteModal } from 'flowbite-react';
import useExitTransition from './useExitTransition';
import './modal.css';

/**
 * Modal Component - Wrapper around Flowbite Modal
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onClose - Function to call when modal should close
 * @param {React.ReactNode} props.children - The content of the modal
 * @param {string} props.size - Modal size ('sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl')
 * @param {'center'|'right'} props.placement - center (default) or full-height right drawer
 * @param {string} props.className - Additional CSS classes for the modal overlay
 * @param {Object} props.theme - Optional Flowbite theme override (not merged with drawer theme when placement is right)
 * @param {Object} props.rest - Additional props passed to Flowbite Modal
 */
const drawerSizeClass = 'm-0 h-full max-h-full rounded-none';

const centerTheme = {
  content: {
    base: 'relative w-full p-4 h-auto',
  },
};

const rightDrawerTheme = {
  root: {
    sizes: Object.fromEntries(
      ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl'].map(
        (key) => [key, drawerSizeClass]
      )
    ),
  },
  content: {
    base: 'relative h-full w-full p-0 md:h-full max-h-full',
    inner:
      'relative flex h-full max-h-full flex-col rounded-none bg-white shadow-xl dark:bg-gray-800 border-l border-gray-200 dark:border-gray-600 sm:rounded-l-lg',
  },
};

const Modal = ({
  show = false,
  onClose,
  children,
  size = 'md',
  className = '',
  placement = 'center',
  theme,
  dismissible,
  ...rest
}) => {
  const isRight = placement === 'right';
  const { isMounted, isClosing, content } = useExitTransition(show, children, {
    enabled: isRight,
  });

  return (
    <FlowbiteModal
      show={isMounted}
      onClose={isClosing ? undefined : onClose}
      size={size}
      position={isRight ? 'center-right' : 'center'}
      dismissible={dismissible !== undefined ? dismissible : isRight}
      className={twMerge(
        isRight && 'p-0 !bg-transparent dark:!bg-transparent right-drawer-modal',
        isRight && isClosing && 'right-drawer-closing',
        !isRight && 'centered-modal',
        className
      )}
      theme={isRight ? rightDrawerTheme : theme || centerTheme}
      {...rest}
    >
      {content}
    </FlowbiteModal>
  );
};

Modal.Header = FlowbiteModal.Header;
Modal.Body = FlowbiteModal.Body;
Modal.Footer = FlowbiteModal.Footer;

export default Modal;
